import { useEffect, useRef, useState } from 'react'
import SelectedCheckmark from './SelectedCheckmark'

// The stored value is the internal sentinel the formula core branches on, never the shown label --
// see README.md ("the Interest dropdown") for the legacy defect this avoids.
export const INTEREST_TYPE_OPTIONS = [
  { value: 'Non-Dutch', label: 'On Drawn Funds' },
  { value: 'Dutch', label: 'On Full Loan Amount' },
]

// Same drawn chevron shape as DisclosureToggle (spec 2.3's glyph is pixel-identical to 2.4's) --
// this control's own open/closed states are NOT SPECIFIED IN FIGMA (spec 5.1), so this generalises
// the closest measured shape rather than inventing one.
function Chevron({ up }) {
  const d = up ? 'M5 9L8 6L11 9' : 'M5 7L8 10L11 7'
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d={d} stroke="#4F5F4A" />
    </svg>
  )
}

// The open list has no drawn frame of its own (spec 5.1); colour/border/shadow are the Allocation
// dropdown's own open list (spec 4.3), sized to this control's own row rhythm rather than copying
// Allocation's dimensions, since "On Full Loan Amount" is longer than Allocation's option strings.
export default function InterestDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const optionRefs = useRef([])
  const selected = INTEREST_TYPE_OPTIONS.find((o) => o.value === value) ?? INTEREST_TYPE_OPTIONS[0]

  useEffect(() => {
    if (!open) return
    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  // Focuses the selected option the moment the list opens -- Escape/arrow keys need a real starting
  // point inside the list, not just the trigger that opened it.
  useEffect(() => {
    if (!open) return
    const idx = Math.max(0, INTEREST_TYPE_OPTIONS.findIndex((o) => o.value === value))
    optionRefs.current[idx]?.focus()
  }, [open, value])

  const closeAndRefocusTrigger = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const handleListKeyDown = (e) => {
    const currentIndex = optionRefs.current.findIndex((el) => el === document.activeElement)
    if (e.key === 'Escape') {
      e.preventDefault()
      closeAndRefocusTrigger()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      optionRefs.current[Math.min(INTEREST_TYPE_OPTIONS.length - 1, currentIndex + 1)]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      optionRefs.current[Math.max(0, currentIndex - 1)]?.focus()
    } else if (e.key === 'Tab') {
      // Back on the trigger first, so the browser's own Tab moves on to the next field from there.
      closeAndRefocusTrigger()
    }
  }

  return (
    <div ref={rootRef} className="relative inline-flex items-center">
      <button
        ref={triggerRef}
        type="button"
        tabIndex={-1}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="group inline-flex items-center gap-x-[6px] outline-none focus-visible:ring-2 focus-visible:ring-brand-green rounded-[4px]"
      >
        <span className="text-[14px] font-bold text-brand-secondary group-hover:text-brand-hover">{selected.label}</span>
        <span className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#EEF3EC] group-hover:bg-[#DDE9D8]">
          <Chevron up={open} />
        </span>
      </button>
      {open ? (
        <ul
          role="listbox"
          aria-label="Interest calculation method"
          onKeyDown={handleListKeyDown}
          className="absolute z-20 top-full left-0 mt-[4px] rounded-[8px] bg-white border border-[#C8D2C3] py-[8px] whitespace-nowrap"
          style={{ boxShadow: '0 0 40px rgba(115,147,94,0.15)' }}
        >
          {INTEREST_TYPE_OPTIONS.map((o, i) => (
            <li key={o.value}>
              <button
                ref={(el) => (optionRefs.current[i] = el)}
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={o.value === value}
                onClick={() => {
                  onChange(o.value)
                  closeAndRefocusTrigger()
                }}
                className={`w-full flex items-center justify-between gap-x-[12px] text-left px-[16px] h-[32px] text-[14px] text-brand-dark bg-white hover:bg-[#EEF3EC] ${
                  o.value === value ? 'font-semibold' : ''
                }`}
              >
                <span>{o.label}</span>
                {o.value === value ? <SelectedCheckmark /> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
